import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const demoOpsDiagnosticsGuide: AdminGuide = {
  id: "demo-ops-diagnostics",
  title: "Demo Ops (diagnostics): verify demo mode health",
  category: "Demo & Partner",
  audience: ["Support", "Ops"],
  summary:
    "Use the Demo Ops page for quick links and diagnostics for demo mode, without polluting customer analytics.",
  keywords: [
    "demo ops",
    "demo mode",
    "diagnostics",
    "preview",
    "usage",
    "anonymous demo",
    "demo usage org",
  ],
  location: "Admin",
  risk: "low",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.DEMO_COMPANY,
    GUIDE_SOURCE_DOCS.USAGE_TRACKING,
  ],
  safetyNotes: [
    "Demo usage is recorded under a dedicated analytics org id and is excluded from default usage analytics unless explicitly filtered.",
    "Support sessions are excluded from customer usage tracking. Demo diagnostics should not require logging in as the customer.",
  ],
  whatThisHelpsYouDo: [
    "Jump to Demo Access Management quickly.",
    "Confirm demo usage is being recorded (logged-in and anonymous).",
    "Spot basic failures (API errors, empty/obviously wrong data) before escalating.",
  ],
  whenToUse: [
    "A teammate reports “demo mode is broken” and you need a quick triage check.",
    "You are preparing for a demo and want to confirm the preview login page is reachable.",
    "You want to sanity-check demo usage diagnostics without affecting customer analytics.",
  ],
  whenNotToUse: [
    "You are investigating real customer engagement or churn (use Usage Analytics / Users activity).",
    "You need to reproduce a real customer issue in their data (use spoofing with permission).",
  ],
  beforeYouStart: [
    "Know which environment you’re checking (Preview vs Production).",
    "Have the user’s Firebase UID if you need to grant demo access.",
  ],
  steps: [
    "Open Admin → **Demo Ops** (`/admin/demo-ops`).",
    "Use **Manage Demo Access** to grant or revoke demo access for a user (if needed).",
    "Use the **Preview Login (/login)** link to quickly verify preview is reachable in a new tab.",
    "Click **Refresh Usage** to reload demo usage diagnostics.",
    "Review the table: rows show demo-enabled users with their recent demo usage minutes and “last seen” date.",
    "If the anonymous demo is being used, expect to see anonymous usage summarized separately (user IDs prefixed like `demo-anon:`).",
  ],
  whatSuccessLooksLike: [
    "Demo Ops loads without an error banner.",
    "Demo Access assignments load (when present).",
    "Demo usage data refreshes successfully when you click Refresh.",
  ],
  troubleshooting: [
    {
      title: "Demo Ops shows “Failed to load demo ops data”",
      body: [
        "Refresh the page and try again.",
        "If the error persists, escalate with the time window you were checking and which environment you were in (Preview/Prod).",
      ],
    },
    {
      title: "Demo usage is always zero",
      body: [
        "Confirm someone has actually entered demo mode in the timeframe you are viewing.",
        "Confirm you are checking the correct environment (Preview vs Production).",
        "Escalate if usage appears to be missing after a reasonable window (usage rollups run on a schedule).",
      ],
    },
  ],
  escalateWhen: [
    "Demo Ops fails to load consistently (include approximate timestamps and environment).",
    "Demo usage appears to be recorded in the wrong place (not isolated to demo analytics).",
    "A user cannot enter or exit demo mode reliably (include Firebase UID and steps to reproduce).",
  ],
  relatedGuides: [
    { label: "Grant or revoke demo access", guideId: "demo-access" },
    { label: "Usage Analytics (Admin)", guideId: "usage-analytics" },
  ],
  relatedAdminPaths: [
    { label: "Demo Ops", path: "/admin/demo-ops" },
    { label: "Demo Access Management", path: "/admin/demo-access" },
    { label: "Usage Analytics", path: "/usage-analytics" },
  ],
};
